import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Clock, X, Sparkles, Check } from 'lucide-react';
import { Button } from './ui/button';

const TrialWelcomeModal = ({ isOpen, onClose, daysRemaining }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    navigate('/subscription');
  };

  const handleContinueTrial = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-fade-in" data-testid="trial-welcome-modal">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 p-6 text-center text-white">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Sparkles className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold mb-1">Welcome to Habit2Goals!</h2>
          <p className="text-white/90">Your 7-day free trial is active</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Trial Status */}
          <div className="flex items-center justify-center gap-2 mb-6 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <Clock className="w-5 h-5 text-blue-500" />
            <span className="font-medium text-blue-700 dark:text-blue-300">
              {daysRemaining} days remaining in your trial
            </span>
          </div>

          {/* Features */}
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            You have full access to all premium features:
          </p>
          <ul className="space-y-2 mb-6">
            {[
              'Unlimited habit tracking',
              'Advanced analytics & charts',
              'CSV & PDF export',
              'Cloud sync across devices'
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleUpgrade}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg"
              data-testid="upgrade-now-button"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Premium - US$2.99/month
            </Button>
            
            <Button
              onClick={handleContinueTrial}
              variant="outline"
              className="w-full"
              data-testid="continue-trial-button"
            >
              Continue with Free Trial
            </Button>
          </div>

          <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-4">
            You can upgrade anytime from the Premium page
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrialWelcomeModal;
